import { graphql, useStaticQuery } from 'gatsby';

interface IPageData {
  node: {
    context: {
      intro: string | null;
      slug: string | null;
      tag: string | null;
      title: {
        text: string | null;
      };
      type: string | null;
      uid: string | null;
    };
  };
}

interface IPageDataProps {
  type: 'single' | 'tree' | 'path';
  slug?: string;
  uid?: string;
  platform?: string;
  returnChildren?: boolean;
}

export const sortChildren = (sortOrder: string[], items) => {
  const childOrder = [];
  sortOrder.forEach((key) => {
    let found = false;
    items.forEach((item) => {
      const slug = item.slug ? item.slug : item.node.context.slug;

      if (!found && slug.includes(key)) {
        childOrder.push(item);
        found = true;
        return false;
      }
      return true;
    });
  });
  return childOrder;
};

/*
Returns various filtered data based on a query of `allSitePage` which is a limited subset of all page data that looks at the `context` fields

Requires passing a `type` and either a `uid` or full page `slug`

Type options:
- `single` - returns the full context for a single page
- `path` - retuns the path (aka slug) for a single page
- `tree` - returns an array of page data for the children of the provided path. When used with `returnChildren: true` also provides the tree for any children as well when applicable
*/
const usePageData = ({
  type = 'single',
  slug,
  uid,
  platform,
  returnChildren = false,
}: IPageDataProps) => {
  const { allSitePage } = useStaticQuery(
    graphql`
      query {
        allSitePage(
          filter: { context: { slug: { nin: [null] } } }
          sort: { fields: context___title }
        ) {
          edges {
            node {
              context {
                intro
                slug
                title
                type
                uid
              }
            }
          }
        }
      }
    `,
  );

  const results = allSitePage.edges.filter((pg: IPageData) => pg.node.context.slug);

  if (slug || uid) {
    if (type === 'single') {
      const page = results.filter((pg: IPageData) => {
        if (slug) {
          return pg.node.context.slug === slug;
        } else {
          return pg.node.context.uid === uid;
        }
      });

      return page[0].node.context;
    }

    if (type === 'path' && platform) {
      const page = results.filter((pg: IPageData) => {
        const path = pg.node.context.slug;
        return path.includes(platform.toString().toLowerCase()) && path.includes(uid);
      });

      return page[0] ? page[0].node.context.slug : false;
    }

    if (type === 'tree') {
      let children = results.filter((pg: IPageData) => {
        if (slug) {
          const limbSlug = pg.node.context.slug;
          // w/o ignore it removes the extra slashes from the regex :(
          // prettier-ignore
          const parentSlug = slug.split('/').join('\\/');
          // prettier-ignore
          const slugPattern = new RegExp(parentSlug + '(\\w|-)+?\\/$', 'g');
          return limbSlug.match(slugPattern);
        } else {
          return false;
        }
      });

      if (slug === '/react/components/forms/') {
        // Sort order
        const formPages = children
          .filter((pg) => !pg.node.context.slug.includes('layout-validation'))
          .map((pg) => pg.node.context.slug);
        children = sortChildren(['layout-validation', ...formPages], children);
      }

      return children
        ? children.map((child) => {
            const { intro, slug, title } = child.node.context;
            return {
              title,
              intro,
              slug,
              children: returnChildren
                ? usePageData({ type: 'tree', slug, returnChildren: true })
                : null,
            };
          })
        : [];
    }
  }

  return false;
};

export default usePageData;
